import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  /** Numérote les accordéons de la page, pour que chacun ait son propre groupe. */
  static compteurDeGroupes = 0;

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#grouperPourExclusivite();
    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('click', this.#handleContentClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Closes the accordion when a click lands inside an element marked with
   * `[data-accordion-close]` (or one of its descendants). Used by markup that
   * provides an explicit close-target region inside the content area.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleContentClick = (event) => {
    if (!this.details.open) return;

    if (!(/** @type {Element} */ (event.target).closest('[data-accordion-close]'))) return;

    this.summary.focus({ preventScroll: true });
    this.details.open = false;
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Un seul volet ouvert à la fois, par accordéon.
   *
   * `<details name="…">` est le mécanisme **natif** des accordéons exclusifs :
   * quand on en ouvre un, le navigateur ferme lui-même celui qui l'était, sans
   * une ligne de JavaScript de notre part. C'est la réponse standard depuis
   * qu'elle existe, et elle vaut mieux qu'un gestionnaire de clic maison qui
   * devrait retrouver les voisins et gérer le clavier.
   *
   * ⚠️ Le nom est posé ici et non dans le gabarit parce qu'**une ligne
   * d'accordéon ne connaît pas le bloc qui la contient** : Liquid n'expose pas
   * le bloc parent à ses enfants. Seule la position dans la page le dit.
   *
   * ⚠️ Le groupe est **par conteneur**, pas global : deux accordéons sur la
   * même page restent indépendants. Un nom commun les rendrait exclusifs
   * entre eux, ce qui n'a aucun sens.
   *
   * ⚠️ On écrit l'attribut, pas la propriété : sur un navigateur trop ancien
   * pour l'exclusivité native, un attribut inconnu est simplement ignoré et
   * l'accordéon retombe sur son comportement d'avant — plusieurs volets
   * ouverts. Aucune erreur, aucune régression.
   */
  #grouperPourExclusivite() {
    const conteneur = this.closest('.accordion');
    if (!conteneur) return;

    if (!conteneur.dataset.groupeAccordeon) {
      AccordionCustom.compteurDeGroupes += 1;
      conteneur.dataset.groupeAccordeon = `accordeon-${AccordionCustom.compteurDeGroupes}`;
    }

    this.details.setAttribute('name', conteneur.dataset.groupeAccordeon);
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.summary.focus();
      this.details.open = false;
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
